import { Fragment, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setEnableMobileMenu } from '../../reducers/ThemeOptions';
import { buildContextualNav, ADMIN_NAV } from '../../orbit/nav';
import { matchPermissions } from '../../orbit/rbac/useHasPermission';
import { useMe } from '../../orbit/me/MeProvider';
import { useBrand } from '../../orbit/brand/BrandProvider';

/**
 * Orbit-Sidebar — Permission-gated, beliebig tief verschachtelbar.
 * Sections:
 *  - "Applications" (kontextuell auf aktive Brand)
 *  - "Administration" (system-level)
 *
 * Ein Eintrag ist sichtbar, wenn seine eigenen Permissions passen ODER er
 * mindestens ein sichtbares Kind (rekursiv) hat. Untergruppen, die die aktive
 * Route enthalten, klappen beim Laden automatisch auf.
 */

const matchItem = (permissions, item) => matchPermissions(permissions, { perms: item.perms, anyOf: item.anyOf });

function isVisible(item, permissions) {
  if (item.children?.length) {
    return matchItem(permissions, item) || item.children.some((c) => isVisible(c, permissions));
  }
  return matchItem(permissions, item);
}

function subtreeHasPath(item, pathname) {
  if (item.to && item.to === pathname) return true;
  return (item.children ?? []).some((c) => subtreeHasPath(c, pathname));
}

const SubMenu = ({ item, permissions, toggleMobileSidebar, depth = 0 }) => {
  const location = useLocation();
  const children = (item.children ?? []).filter((c) => isVisible(c, permissions));
  const hasSubmenu = children.length > 0;
  const containsActive = subtreeHasPath(item, location.pathname);
  const [isOpen, setIsOpen] = useState(containsActive);

  const isActive = location.pathname === item.to || (hasSubmenu && containsActive);

  const onClick = (e) => {
    if (hasSubmenu) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen((o) => !o);
    } else if (item.to) {
      toggleMobileSidebar();
    }
  };

  // Verschachtelte Untergruppen (Tiefe ≥ 2) zusätzlich einrücken.
  const linkStyle = depth >= 2 ? { paddingLeft: 12 + depth * 10 } : undefined;

  return (
    <li className={`metismenu-item ${isActive ? 'active' : ''}`}>
      <Link
        to={item.to ?? '#'}
        className={`metismenu-link ${isActive ? 'active' : ''}`}
        style={linkStyle}
        onClick={onClick}
      >
        {item.icon && <i className={`metismenu-icon ${item.icon}`} />}
        {item.label}
        {hasSubmenu && (
          <i className={`metismenu-state-icon pe-7s-angle-${isOpen ? 'up' : 'down'}`} />
        )}
      </Link>
      {hasSubmenu && (
        <ul className={`metismenu-container ${isOpen ? 'visible' : ''}`}>
          {children.map((child) => (
            <SubMenu
              key={child.label}
              item={child}
              permissions={permissions}
              toggleMobileSidebar={toggleMobileSidebar}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const Nav = () => {
  const enableMobileMenu = useSelector((s) => s.ThemeOptions.enableMobileMenu);
  const dispatch = useDispatch();
  const me = useMe();
  const brand = useBrand();
  const { brandSlug: paramSlug } = useParams();
  const [filter, setFilter] = useState('');

  const toggleMobileSidebar = () => {
    if (enableMobileMenu) dispatch(setEnableMobileMenu(false));
  };

  const slug = paramSlug ?? brand.currentBrand?.slug;
  const contextualItems = slug ? buildContextualNav(slug) : [];

  // Volltext-Filter (bei vielen Einträgen): behält Gruppen, deren Label ODER ein
  // Nachkommen-Label passt; flach in der Anzeige egal, Sichtbarkeit rekursiv.
  const q = filter.trim().toLowerCase();
  const labelMatches = (item) => {
    if (!q) return true;
    if (item.label?.toLowerCase().includes(q)) return true;
    return (item.children ?? []).some(labelMatches);
  };

  const renderMenu = (items) =>
    items
      .filter((item) => isVisible(item, me.permissions) && labelMatches(item))
      .map((item) => (
        <SubMenu
          key={item.label}
          item={item}
          toggleMobileSidebar={toggleMobileSidebar}
          permissions={me.permissions}
        />
      ));

  const visibleContextual = contextualItems.filter((i) => isVisible(i, me.permissions));
  const visibleAdmin = ADMIN_NAV.filter((i) => isVisible(i, me.permissions));

  return (
    <Fragment>
      <div className="vertical-nav-menu">
        {visibleAdmin.length + visibleContextual.length > 8 && (
          <div className="px-3 pt-2 pb-1">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Menü filtern…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Menü filtern"
            />
          </div>
        )}

        {visibleContextual.length > 0 && (
          <>
            <h5 className="app-sidebar__heading">Applications</h5>
            <ul className="metismenu-container">{renderMenu(contextualItems)}</ul>
          </>
        )}

        {visibleAdmin.length > 0 && (
          <>
            <h5 className="app-sidebar__heading">Administration</h5>
            <ul className="metismenu-container">{renderMenu(ADMIN_NAV)}</ul>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default Nav;
